import { Button, Chip, Input, Modal, Select, Spinner } from '@sjuni/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { TranslatableField } from '@/components/form/TranslatableField';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import {
  useResourceAction,
  useResourceList,
  useResourceRecord,
  useSaveResource,
} from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';

import {
  type FacultyRow,
  type FacultyStatisticInput,
  type FacultyStatisticRow,
  STATISTIC_KINDS,
  type StatisticKind,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/faculties/:id/statistics`.
 *
 * The module that behaves unlike every other one here. docs/03-DATA-MODEL.md §4
 * keeps the numbers *per academic year* rather than overwriting them — "last
 * year's numbers stay on the record" — so filing this year's figures adds rows
 * rather than replacing them. That is the opposite of what an editor expects
 * from a form, which is why the year is the leading column and the screen says
 * so in as many words.
 */
export interface FacultyStatisticsScreenProps {
  facultyId: string;
}

export function FacultyStatisticsScreen({ facultyId }: FacultyStatisticsScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can } = useAuth();

  const [editing, setEditing] = useState<FacultyStatisticRow | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<FacultyStatisticRow | null>(null);

  const faculty = useResourceRecord<FacultyRow>('faculties', Number(facultyId));

  const { data, isPending, isError } = useResourceList<FacultyStatisticRow>('faculty-statistics', {
    faculty_id: Number(facultyId),
    per_page: 100,
    sort: '-academic_year',
  });

  const act = useResourceAction<FacultyStatisticRow>('faculty-statistics');

  const mayManage = can('faculties.update');

  const columns: Column<FacultyStatisticRow>[] = [
    {
      key: 'academic_year',
      label: t('academic.statistics.columns.year'),
      sortable: true,
      render: (row) => (
        <span className="font-semibold text-primary">
          {row.academic_year ?? t('academic.statistics.noYear')}
        </span>
      ),
    },
    {
      key: 'kind',
      label: t('academic.statistics.columns.kind'),
      render: (row) => <Chip tone="navy">{t(`academic.statistics.kinds.${row.kind}`)}</Chip>,
    },
    {
      key: 'label',
      label: t('academic.statistics.columns.label'),
      render: (row) => <span className="text-secondary">{valueFor(row.label, locale)}</span>,
    },
    {
      key: 'value',
      label: t('academic.statistics.columns.value'),
      sortable: true,
      align: 'end',
      render: (row) => (
        <span className="font-semibold text-primary">{row.value.toLocaleString()}</span>
      ),
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) =>
        mayManage ? (
          <span className="inline-flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Pencil size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setEditing(row);
              }}
            >
              {t('news.categories.edit')}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setDeleting(row);
              }}
            >
              {t('common.delete')}
            </Button>
          </span>
        ) : null,
    },
  ];

  if (faculty.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">{t('academic.statistics.title')}</h1>
          <p className="mt-1 text-body-sm text-secondary">
            {faculty.data ? valueFor(faculty.data.data.name, locale) : ''}
          </p>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/faculties/${facultyId}`)}
        >
          {t('common.back')}
        </Button>
      </div>

      {/*
        Said plainly, because it is the one thing about this screen that
        surprises people: adding this year's figures does not replace last
        year's, and the public page reads whichever year it is asked for.
      */}
      <p className="rounded-card border-s-4 border-gold-500 bg-surface-alt p-3 text-caption text-secondary">
        {t('academic.statistics.perYearNotice')}
      </p>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      {mayManage ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => setEditing(null)}
          >
            {t('academic.statistics.add')}
          </Button>
        </div>
      ) : null}

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        onOpen={mayManage ? (row) => setEditing(row) : undefined}
        emptyTitle={t('academic.statistics.empty.title')}
        emptyBody={t('academic.statistics.empty.body')}
      />

      {editing !== undefined ? (
        <StatisticDialog
          statistic={editing}
          facultyId={Number(facultyId)}
          onClose={() => setEditing(undefined)}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('academic.statistics.delete.title')}
        body={t('academic.statistics.delete.body', {
          label: deleting ? valueFor(deleting.label, locale) : '',
        })}
        confirmLabel={t('common.delete')}
        // No `deleted_at` on this table — it cascades from its faculty (§4), so
        // a soft delete on the part would outlive the whole.
        reversible={false}
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            act.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />
    </div>
  );
}

interface Fields {
  kind: StatisticKind;
  label: TranslatedValue;
  value: number;
  academic_year: string;
  sort_order: number;
}

function StatisticDialog({
  statistic,
  facultyId,
  onClose,
}: {
  statistic: FacultyStatisticRow | null;
  facultyId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveResource<FacultyStatisticRow, FacultyStatisticInput>('faculty-statistics');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Fields>({
    defaultValues: {
      kind: statistic?.kind ?? 'students',
      label: statistic?.label ?? {},
      value: statistic?.value ?? 0,
      academic_year: statistic?.academic_year ?? currentAcademicYear(),
      sort_order: statistic?.sort_order ?? 0,
    },
  });

  const label = watch('label');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await save.mutateAsync({
        id: statistic?.id,
        input: {
          faculty_id: facultyId,
          kind: values.kind,
          label: values.label,
          value: Number(values.value),
          academic_year: values.academic_year.trim() === '' ? null : values.academic_year.trim(),
          sort_order: Number(values.sort_order),
        },
      });

      onClose();
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'kind',
        'label',
        'label.ka',
        'value',
        'academic_year',
        'sort_order',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={statistic ? t('academic.statistics.edit') : t('academic.statistics.add')}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,620px)]"
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <TranslatableField
          label={t('academic.statistics.columns.label')}
          hint={t('academic.statistics.fields.labelHint')}
          name="label"
          value={label}
          onChange={(next) => setValue('label', next, { shouldDirty: true })}
          error={errors.label?.message}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('academic.statistics.columns.kind')}
            {...register('kind')}
            options={STATISTIC_KINDS.map((kind) => ({
              value: kind,
              label: t(`academic.statistics.kinds.${kind}`),
            }))}
          />

          <Input
            {...register('value', { valueAsNumber: true })}
            type="number"
            min={0}
            label={t('academic.statistics.columns.value')}
            error={errors.value?.message}
          />

          <Input
            {...register('academic_year')}
            label={t('academic.statistics.columns.year')}
            hint={t('academic.statistics.fields.yearHint')}
            placeholder="2025-2026"
            error={errors.academic_year?.message}
          />

          <Input
            {...register('sort_order', { valueAsNumber: true })}
            type="number"
            min={0}
            label={t('content.fields.order')}
            hint={t('content.fields.orderHint')}
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>

          <SubmitButton loading={isSubmitting}>{t('editor.publishing.save')}</SubmitButton>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The academic year we are in, as `2025-2026`.
 *
 * It turns over in the autumn rather than in January — a figure filed in
 * October belongs to the year that has just started, and defaulting to the
 * calendar year would file half of them under the wrong one.
 */
function currentAcademicYear(): string {
  const now = new Date();
  // Months are zero-based; 8 is September.
  const start = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;

  return `${start}-${start + 1}`;
}
